/**
 * The seat login guard's rows (WI 34592). The Control Center compares a local
 * seat home's signed-in Claude account with its seat-registry.json
 * expectedLogin (tools/scripts/ryn/windows/control-center/lib/seat-login.js);
 * this turns that check into Needs attention rows. A wrong login is a warning
 * under Accounts, on top of whatever else the seat is doing. Seats with no
 * expected login share ONE parked row that is listed but never counted.
 */
import type { Alert, SeatSignal } from './attention.logic';

export type SeatLoginStatus =
  | 'match'
  | 'mismatch'
  | 'unknown'
  | 'no-login'
  | 'unverified';

/** Emails only; the CC never forwards a token or any other login field. */
export interface SeatLogin {
  status: SeatLoginStatus;
  expected: string | null;
  actual: string | null;
}

export function seatLoginAlerts(seats: readonly SeatSignal[]): Alert[] {
  const out: Alert[] = [];
  const unknown: string[] = [];
  for (const s of seats) {
    const login = s.login;
    if (login?.status === 'unknown') unknown.push(s.instanceId);
    if (login?.status !== 'mismatch') continue;
    out.push({
      id: `seat|${s.instanceId}|login`,
      severity: 'warn',
      kind: 'seat',
      category: 'account',
      title: `${s.instanceId} signed in as ${login.actual}`,
      detail: `${s.host ?? 'unknown host'} · expected ${login.expected} · burning the wrong account's window; /login as ${login.expected}`,
      action: { type: 'open-seat', label: 'Open seat', target: s.instanceId },
      weight: 500,
    });
  }
  if (unknown.length) {
    const ids = [...unknown].sort();
    out.push({
      id: 'seat-login|unknown',
      severity: 'warn',
      kind: 'seat',
      category: 'account',
      title: `${ids.length} seat${ids.length === 1 ? ' has' : 's have'} no expected login`,
      detail: `${ids.join(', ')} · add expectedLogin in seat-registry.json to guard them`,
      action: { type: 'open-seat', label: 'Open seat', target: ids[0] },
      weight: 0,
      parked: true,
    });
  }
  return out;
}
